/**
 * GL Product Gallery
 * Amazon-style: one main image + scrollable thumbnail strip.
 * Features: thumbnails, arrows, swipe, keyboard, variant switching, lightbox.
 */

class GLProductGallery {
  constructor(el) {
    this.el = el;
    this.sectionId = el.dataset.section;

    // Core elements
    this.slides     = Array.from(el.querySelectorAll('.gl-gallery__slide'));
    this.thumbs     = Array.from(el.querySelectorAll('.gl-gallery__thumb'));
    this.thumbsTrack = el.querySelector('.gl-gallery__thumbs');
    this.prevBtn    = document.getElementById('GLPrev-' + this.sectionId);
    this.nextBtn    = document.getElementById('GLNext-' + this.sectionId);
    this.zoomBtn    = document.getElementById('GLZoom-' + this.sectionId);
    this.counterEl  = document.getElementById('GLCurrent-' + this.sectionId);
    this.lightboxEl = document.getElementById('GLLightbox-' + this.sectionId);

    // State
    this.currentIndex  = this._getInitialIndex();
    this.lightboxIndex = 0;
    this.variantMedia  = this._parseVariantMedia();

    if (this.slides.length === 0) return;

    this._bindGalleryEvents();
    this._initVariantWatcher();
    if (this.lightboxEl) this._initLightbox();

    this._updateCounter();
  }

  // ─── SETUP ───────────────────────────────────────────────

  _getInitialIndex() {
    const active = this.el.querySelector('.gl-gallery__slide.is-active');
    return active ? (parseInt(active.dataset.index, 10) || 0) : 0;
  }

  _parseVariantMedia() {
    try {
      return JSON.parse(this.el.dataset.variantMedia || '{}');
    } catch (_) {
      return {};
    }
  }

  _bindGalleryEvents() {
    // Thumbnail clicks
    this.thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        this.goTo(parseInt(thumb.dataset.index, 10));
      });
    });

    // Arrow navigation
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());

    // Zoom button opens lightbox at current slide
    if (this.zoomBtn) {
      this.zoomBtn.addEventListener('click', () => this._openLightbox(this.currentIndex));
    }

    // Click on main image to zoom
    this.slides.forEach((slide, i) => {
      const btn = slide.querySelector('.gl-gallery__img-btn');
      if (btn) btn.addEventListener('click', () => this._openLightbox(i));
    });

    // Keyboard arrows (only when lightbox is closed)
    document.addEventListener('keydown', (e) => {
      if (this.lightboxEl && this.lightboxEl.classList.contains('is-open')) return;
      if (e.key === 'ArrowLeft')  { e.preventDefault(); this.prev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.next(); }
    });

    // Swipe on main image area
    const mainEl = this.el.querySelector('.gl-gallery__main');
    if (mainEl) this._addSwipe(mainEl, () => this.prev(), () => this.next());
  }

  // ─── NAVIGATION ──────────────────────────────────────────

  goTo(index) {
    const total = this.slides.length;
    if (total === 0) return;
    index = ((index % total) + total) % total;

    // Update slides
    this.slides.forEach((s, i) => s.classList.toggle('is-active', i === index));

    // Update thumbnails
    this.thumbs.forEach((t, i) => {
      const active = i === index;
      t.classList.toggle('is-active', active);
      t.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    // Scroll active thumb into view
    if (this.thumbsTrack && this.thumbs[index]) {
      this._scrollThumbIntoView(this.thumbs[index]);
    }

    this.currentIndex = index;
    this._updateCounter();
  }

  prev() { this.goTo(this.currentIndex - 1); }
  next() { this.goTo(this.currentIndex + 1); }

  _updateCounter() {
    if (this.counterEl) this.counterEl.textContent = this.currentIndex + 1;
  }

  _scrollThumbIntoView(thumb) {
    if (!this.thumbsTrack) return;
    const offset = thumb.offsetLeft + thumb.offsetWidth / 2 - this.thumbsTrack.offsetWidth / 2;
    this.thumbsTrack.scrollTo({ left: Math.max(0, offset), behavior: 'smooth' });
  }

  // ─── SWIPE SUPPORT ───────────────────────────────────────

  _addSwipe(el, onSwipeRight, onSwipeLeft) {
    let startX = 0, startY = 0;
    el.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });
    el.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
        dx < 0 ? onSwipeLeft() : onSwipeRight();
      }
    }, { passive: true });
  }

  // ─── VARIANT SWITCHING ───────────────────────────────────

  _initVariantWatcher() {
    // Watch the hidden [name="id"] input for value changes (Dawn updates it on variant select)
    const form = document.querySelector('#product-form-' + this.sectionId);
    if (!form) return;

    const variantInput = form.querySelector('[name="id"]');
    if (!variantInput) return;

    const handleVariantChange = () => {
      const mediaId = String(this.variantMedia[variantInput.value] || '');
      if (!mediaId) return;
      const target = this.el.querySelector('[data-media-id="' + mediaId + '"]');
      if (target) this.goTo(parseInt(target.dataset.index, 10));
    };

    // MutationObserver catches programmatic value updates
    new MutationObserver(handleVariantChange).observe(variantInput, {
      attributes: true,
      attributeFilter: ['value'],
    });

    // Also catch direct change events (some themes fire these)
    variantInput.addEventListener('change', handleVariantChange);

    // Listen on variant picker radio/select changes too
    form.addEventListener('change', (e) => {
      if (e.target && e.target.name !== 'id') {
        setTimeout(handleVariantChange, 50); // slight delay for Dawn to update hidden input
      }
    });
  }

  // ─── LIGHTBOX ────────────────────────────────────────────

  _initLightbox() {
    const lb = this.lightboxEl;
    if (lb && lb.parentNode !== document.body) {
      document.body.appendChild(lb);
    }
    const closeBtn = lb.querySelector('.gl-lightbox__close');
    const prevBtn  = lb.querySelector('.gl-lightbox__nav--prev');
    const nextBtn  = lb.querySelector('.gl-lightbox__nav--next');
    const countTotal = lb.querySelector('.gl-lightbox__count-total');

    // Set total count
    if (countTotal) countTotal.textContent = this.slides.length;

    // Close handlers
    if (closeBtn) closeBtn.addEventListener('click', () => this._closeLightbox());
    lb.addEventListener('click', (e) => { if (e.target === lb) this._closeLightbox(); });

    // Nav handlers
    const lbPrev = () => {
      this.lightboxIndex = (this.lightboxIndex - 1 + this.slides.length) % this.slides.length;
      this._updateLightboxImage();
    };
    const lbNext = () => {
      this.lightboxIndex = (this.lightboxIndex + 1) % this.slides.length;
      this._updateLightboxImage();
    };
    if (prevBtn) prevBtn.addEventListener('click', lbPrev);
    if (nextBtn) nextBtn.addEventListener('click', lbNext);

    // Keyboard inside lightbox
    document.addEventListener('keydown', (e) => {
      if (!lb.classList.contains('is-open')) return;
      if (e.key === 'Escape')     { e.preventDefault(); this._closeLightbox(); }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); lbPrev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); lbNext(); }
    });

    // Swipe inside lightbox
    this._addSwipe(lb, lbPrev, lbNext);
  }

  _openLightbox(index) {
    if (!this.lightboxEl) return;
    // Only open for image slides
    const slide = this.slides[index];
    if (slide && !slide.querySelector('img')) return; // skip video/model slides
    this.lightboxIndex = index;
    this._updateLightboxImage();
    this.lightboxEl.classList.add('is-open');
    this.lightboxEl.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  _closeLightbox() {
    if (!this.lightboxEl) return;
    this.lightboxEl.classList.remove('is-open');
    this.lightboxEl.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  _updateLightboxImage() {
    const lb = this.lightboxEl;
    if (!lb) return;
    const lbImg      = lb.querySelector('.gl-lightbox__img');
    const countEl    = lb.querySelector('.gl-lightbox__count-current');
    const slide      = this.slides[this.lightboxIndex];
    if (!slide) return;

    const srcImg = slide.querySelector('img');
    if (lbImg && srcImg) {
      // Request high-resolution image from Shopify CDN
      let src = srcImg.src;
      // Strip any existing width/crop params and request max resolution
      src = src.split('?')[0];
      lbImg.src = src + '?width=2000';
      lbImg.alt = srcImg.alt || '';
    }

    if (countEl) countEl.textContent = this.lightboxIndex + 1;
  }
}

// ─── INIT ────────────────────────────────────────────────────

function initGLGalleries() {
  document.querySelectorAll('.gl-gallery:not([data-gl-init])').forEach((el) => {
    el.setAttribute('data-gl-init', 'true');
    new GLProductGallery(el);
  });
}

// Handle both sync and async (defer) script loading
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGLGalleries);
} else {
  initGLGalleries();
}
